
import type { MineResult, Mining } from "../net/messages";
import { elem, styleInstaller } from "./chrome";

export interface MiningActions {
  /** Start swinging at the vein ahead, or stop swinging. One call for both;
   *  the only request field is this facing and the server chooses the vein. */
  onMine?: (heading: number) => void;
}

/** `Mining.state`. Wire values, fixed by the schema. */
export const MINING_IDLE = 0;
export const MINING_SWINGING = 1;

/** `MineResult.outcome`. Wire values, fixed by the schema. */
export const MINE_ORE = 0;
export const MINE_NOTHING = 1;
export const MINE_VEIN_GONE = 2;
export const MINE_NO_PICK_LEFT = 3;
export const MINE_STOPPED = 4;

export function describeSwing(result: MineResult): string {
  switch (result.outcome) {
    case MINE_ORE: {
      const name = result.name || `ore #${result.vnum}`;
      return `The mining has been successful. ${result.count} × ${name} came loose — it is on the ground.`;
    }
    case MINE_NOTHING:
      // `mining.cpp:378`, verbatim.
      return "The mining has failed.";
    case MINE_VEIN_GONE:
      // `mining.cpp:365`, verbatim -- the same line the server sends when
      // `chLoad` (the vein) is gone.
      return "Nothing to mine here.";
    case MINE_NO_PICK_LEFT:
      // `mining.cpp:358`, verbatim.
      return "You cannot mine without a Pick.";
    case MINE_STOPPED:
      // `mining_cancel` (`char.cpp:3793`), verbatim.
      return "Mining is finished.";
    default:
      return "Nothing came of it.";
  }
}

/** What the button should say for a given swing state. */
export function buttonLabel(state: number): string {
  return state === MINING_SWINGING ? "Stop" : "Mine";
}

const STYLE_ID = "mining-style";

const STYLE = `
.mining {
  position: fixed;
  right: 12px;
  top: 470px;
  z-index: 58;
  width: 196px;
  padding: 9px 11px 10px;
  border-radius: 10px;
  background: rgba(12, 14, 18, 0.86);
  border: 1px solid rgba(200, 170, 120, 0.24);
  pointer-events: auto;
  font: 12px/1.45 system-ui, sans-serif;
  color: #d8dee6;
}
.mining[hidden] { display: none; }
.mining-title {
  font-size: 0.66rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #c4a884;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 5px;
}
.mining-state { color: #d4bb9f; }
.mining-none { color: #8fa0b4; margin: 4px 0 6px; }
.mining-swings { margin-bottom: 4px; }
.mining-window {
  height: 7px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.1);
  overflow: hidden;
  margin: 5px 0;
}
.mining-window[hidden] { display: none; }
.mining-window-fill { height: 100%; width: 0; background: #d8a86a; }
.mining-last { color: #b8c6d8; font-size: 11px; min-height: 2.6em; }
.mining-button {
  width: 100%;
  margin-top: 6px;
  padding: 5px 0;
  border-radius: 6px;
  border: 1px solid rgba(200, 170, 120, 0.34);
  background: rgba(200, 170, 120, 0.1);
  color: #f0dcc0;
  font: inherit;
  cursor: pointer;
}
.mining-button:hover:not(:disabled) { background: rgba(200, 170, 120, 0.22); }
.mining-button:disabled { opacity: 0.35; cursor: default; }
`;

const installStyle = styleInstaller(STYLE_ID, STYLE);

export class MiningPanel {
  private readonly root: HTMLElement;
  private readonly state: HTMLElement;
  private readonly none: HTMLElement;
  private readonly swings: HTMLElement;
  private readonly window_: HTMLElement;
  private readonly windowFill: HTMLElement;
  private readonly last: HTMLElement;
  private readonly button: HTMLButtonElement;
  private readonly now: () => number;

  /** The swing exactly as last sent. What the button does is decided by this
   *  and never by a flag of this panel's own -- a swing can end without the
   *  player touching anything, when the vein crumbles or the interval is up. */
  private drawn: Mining | null = null;

  /** When the swinging frame arrived, on this client's clock, and how long it
   *  said was left. Used for drawing only: the server measures the interval
   *  from its own tick and neither number ever reaches the wire. */
  private startedAtMs = 0;
  private startedWithMs = 0;

  /** The direction of the last vein click. An angle is all `Mine` carries: the
   *  server decides whether a standing vein is actually ahead and in reach. */
  private facing = 0;

  private last_: MineResult | null = null;

  constructor(
    container: HTMLElement,
    private readonly actions: MiningActions = {},
    now?: () => number,
  ) {
    this.now = now ?? (() => performance.now());
    const doc = container.ownerDocument;
    installStyle(doc);

    this.root = elem(doc, "div", "mining");
    this.root.hidden = true;

    const title = elem(doc, "div", "mining-title");
    const heading = doc.createElement("span");
    heading.textContent = "Mining";
    this.state = elem(doc, "span", "mining-state");
    title.append(heading, this.state);

    this.none = elem(doc, "div", "mining-none", "Click an ore vein to mine it.");

    this.swings = elem(doc, "div", "mining-swings");
    this.swings.hidden = true;

    this.window_ = elem(doc, "div", "mining-window");
    this.window_.hidden = true;
    this.windowFill = elem(doc, "div", "mining-window-fill");
    this.window_.append(this.windowFill);

    this.last = elem(doc, "div", "mining-last");

    this.button = doc.createElement("button");
    this.button.type = "button";
    this.button.className = "mining-button";
    this.button.textContent = buttonLabel(MINING_IDLE);
    this.button.addEventListener("click", () => this.press());

    this.root.append(title, this.none, this.swings, this.window_, this.last, this.button);
    container.append(this.root);
  }

  get showing(): boolean {
    return !this.root.hidden;
  }

  /** The state this panel is currently drawing. For tests, and for a harness
   *  waiting out an interval it cannot predict. */
  get swingState(): number {
    return this.drawn === null ? MINING_IDLE : this.drawn.state;
  }

  /** Which way the next press will face. */
  get heading(): number {
    return this.facing;
  }

  setVisible(visible: boolean): void {
    this.root.hidden = !visible;
  }

  toggle(): void {
    this.root.hidden = !this.root.hidden;
  }

  aimAt(heading: number): void {
    this.facing = heading;
    this.setVisible(true);
    this.press();
  }

  /** Start, or stop, from anywhere -- the key handler uses this so that one
   *  gesture and one button cannot disagree about which they are. */
  press(): void {
    this.actions.onMine?.(this.facing);
  }

  update(swing: Mining): void {
    const wasSwinging = this.swingState === MINING_SWINGING;
    this.drawn = swing;

    if (swing.state === MINING_SWINGING) {
      if (!wasSwinging) {
        this.startedAtMs = this.now();
        this.startedWithMs = Math.max(1, swing.remaining_ms);
      }
    }

    const swinging = swing.state === MINING_SWINGING;
    this.none.hidden = swinging;
    this.swings.hidden = !swinging;
    this.window_.hidden = !swinging;
    if (swinging) {
      this.swings.textContent = `${swing.swings} swings`;
    }

    this.state.textContent = swinging ? "swinging" : "";
    this.button.textContent = buttonLabel(swing.state);
    // Never greyed. Starting is what a player does next when idle, and stopping
    // is what they do next when swinging; there is no state in which pressing
    // this is meaningless, and a refusal -- no pick, too far, on a horse --
    // is a `Notice` the server sends rather than something drawn here.
    this.button.disabled = false;
  }

  /** Say what came of a swing. One sentence, replaced rather than accumulated:
   *  a feed of swings is what the chat log is for. */
  resolved(result: MineResult): void {
    this.last_ = result;
    this.last.textContent = describeSwing(result);
  }

  /** What the last swing came to, or null if none has yet. For tests and for a
   *  harness that has to wait out an interval it cannot predict. */
  get lastResult(): MineResult | null {
    return this.last_;
  }

  tick(): void {
    if (this.drawn === null || this.drawn.state !== MINING_SWINGING) return;
    const elapsed = this.now() - this.startedAtMs;
    const fraction = Math.max(0, Math.min(1, 1 - elapsed / this.startedWithMs));
    this.windowFill.style.width = `${Math.round(fraction * 100)}%`;
  }
}
